import { useEffect, useRef, useState } from 'react';
import {
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES,
  NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES,
  STOP_RESPONSE_GATE_FALLBACK_MS
} from '../constants/simulationConstants';
import { isIOS, postMessage } from '../helpers/utils';
import { useSimulationContext } from '../providers/SimulationProvider';
import useAudioQueue from './useAudioQueue';
import useResponseTimeout from './useResponseTimeout';
import nudgytAgentConfig from '../config/nudgytAgentConfig';
import createNudgytAgentChatSocket from '../websocket/nudgytAgentChatSocket';

/**
 * Custom hook for managing Nudgyt Agent WebSocket connection and AI interactions
 * This hook handles:
 * - WebSocket connection lifecycle (connect, reconnect, disconnect)
 * - AI message streaming (text, audio, emotion, viseme data)
 * - Audio queue management for sequential playback
 * - Response timeout detection
 * - Session token rotation: defers closing/reopening the socket until
 *   `response_ended` (or error/cancel/timeout) when a new token arrives mid-reply
 */
function useThreeNudgytAgentClient() {
  const [isWsConnected, setIsWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const isIntentionalCloseRef = useRef(false);
  const nudgytSessionTokenRef = useRef(null);
  const isEnableHookRef = useRef(false);
  const lastConnectedTokenRef = useRef(null);
  const deferredTokenReconnectRef = useRef(false);
  const aiResponseInFlightRef = useRef(false);
  const flushDeferredTokenReconnectRef = useRef(() => {});
  /**
   * Set while a stop is pending the server's `cancelled` echo. Content chunks
   * of the stopped reply are dropped rather than played or forwarded — the
   * in-flight flag alone cannot do this, because the learner's next message
   * sets it back to true and lets stragglers through behind it.
   */
  const stoppedResponseRef = useRef(false);
  const stopResponseFallbackTimeoutRef = useRef(null);

  const { audioRef, enqueueAudio, clearAudioQueue } = useAudioQueue();

  const {
    emotion,
    userText,
    isAiTalking,
    facialData,
    documentIds,
    userEmotion,
    isSceneReady,
    characterIds,
    isAvatarHidden,
    userInteract,
    nudgytSessionToken,
    isClientInitialized,
    isEmotionDetectionReady,
    isStopResponseRequested,
    setEmotion,
    setUserText,
    setIsAiTalking,
    setFacialData,
    setIsClientInitialized,
    setDocumentIds,
    setUserInteract,
    setSelectedCharacterId,
    setIsStopResponseRequested
  } = useSimulationContext();

  const hasSession = Boolean(nudgytSessionToken);
  nudgytSessionTokenRef.current = nudgytSessionToken;
  isEnableHookRef.current = hasSession;

  console.debug('[DEBUG] Hook state updated', {
    hasNudgytSessionToken: !!nudgytSessionToken,
    isEnableHook: isEnableHookRef.current,
    isWsConnected,
    isClientInitialized,
    isSceneReady
  });

  /**
   * Reopens the gate closed by a stop request, so the next reply streams
   * normally.
   */
  const clearStoppedResponseGate = () => {
    stoppedResponseRef.current = false;
    if (stopResponseFallbackTimeoutRef.current) {
      clearTimeout(stopResponseFallbackTimeoutRef.current);
      stopResponseFallbackTimeoutRef.current = null;
    }
  };

  /**
   * Resets the client state to default values
   * Clears talking state, user text, and facial animation data
   */
  const resetClientState = () => {
    setIsAiTalking(false);
    setUserText('');
    setFacialData([]);
    setDocumentIds([]);
  };

  /**
   * Response timeout handler
   * Detects when Nudgyt Agent AI fails to respond within 30 seconds
   */
  const { startResponseTimeout, clearResponseTimeout } = useResponseTimeout({
    onTimeout: () => {
      resetClientState();
      // Websocket reconnect config
      aiResponseInFlightRef.current = false;
      flushDeferredTokenReconnectRef.current();
    }
  });

  // Handle websocket reconnection logic
  const handleReconnection = () => {
    reconnectAttemptsRef.current += 1;

    reconnectTimeoutRef.current = setTimeout(() => {
      connectWebSocket();
    }, nudgytAgentConfig.reconnectDelay);
  };

  /**
   * Establishes WebSocket connection to Nudgyt Agent server
   * Sets up event handlers for connection lifecycle and message handling
   */
  const connectWebSocket = () => {
    console.debug('[DEBUG] connectWebSocket called', {
      isEnableHook: isEnableHookRef.current,
      hasToken: !!nudgytSessionTokenRef.current
    });

    if (!isEnableHookRef.current) {
      console.debug('[DEBUG] Early return: isEnableHook is false');
      return;
    }

    const accessTokenForConnection = nudgytSessionTokenRef.current;

    if (!accessTokenForConnection) {
      console.error('[Nudgyt Agent Client] Missing auth: nudgytSessionToken');
      return;
    }

    console.debug('[DEBUG] Proceeding to create WebSocket connection', {
      baseUrl: nudgytAgentConfig.baseUrl,
      hasAccessToken: !!accessTokenForConnection
    });

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const ws = createNudgytAgentChatSocket({
      base: nudgytAgentConfig.baseUrl,
      sessionToken: accessTokenForConnection
    });

    if (!ws) return;

    ws.onopen = () => {
      console.debug('[DEBUG] WebSocket opened successfully');

      const latestToken = nudgytSessionTokenRef.current;
      if (latestToken && latestToken !== accessTokenForConnection) {
        console.debug(
          '[DEBUG] Token changed during connection, closing and reconnecting'
        );
        isIntentionalCloseRef.current = true;
        ws.close(1000, 'Superseded by newer session token');
        isIntentionalCloseRef.current = false;
        reconnectAttemptsRef.current = 0;
        if (isEnableHookRef.current && nudgytSessionTokenRef.current)
          connectWebSocket();

        return;
      }

      lastConnectedTokenRef.current = accessTokenForConnection;

      console.debug('[DEBUG] WebSocket connection established', {
        token: accessTokenForConnection?.substring(0, 10) + '...'
      });
      setIsWsConnected(true);
      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
    };

    /**
     * WebSocket onmessage event handler
     * Processes incoming AI messages (text, audio, emotion, viseme data)
     */
    ws.onmessage = (ev) => {
      let aiMessage = JSON.parse(ev.data);

      // Gate only stray text tokens — audio/visemes may arrive before response_start.
      const isGatedTextToken =
        !aiResponseInFlightRef.current &&
        aiMessage.type === NUDGYT_AGENT_MESSAGE_TYPES.TEXT_TOKEN;

      if (isGatedTextToken) return;

      // Chunks still arriving from a reply the learner stopped. Returning here
      // is before the postMessage forward below, so nothing reaches the parent
      // either. Ordered delivery means every straggler precedes the
      // `cancelled` echo that reopens the gate.
      if (
        stoppedResponseRef.current &&
        NUDGYT_AGENT_STOPPABLE_RESPONSE_TYPES.includes(aiMessage.type)
      ) {
        return;
      }

      const validTypes = Object.values(NUDGYT_AGENT_MESSAGE_TYPES);

      if (
        typeof aiMessage !== 'object' ||
        !aiMessage.type ||
        !validTypes.includes(aiMessage.type)
      ) {
        console.warn(
          '[Nudgyt Agent Client Warning] Unknown message type:',
          aiMessage
        );
      }

      let flushDeferredAfterForward = false;

      switch (aiMessage.type) {
        case NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY:
          startResponseTimeout();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_START:
          aiResponseInFlightRef.current = true;
          // A reply that starts after a stop is a new one, and every straggler
          // from the stopped reply is already behind us in the stream. This is
          // what reopens the gate when the agent skips the `cancelled` echo.
          clearStoppedResponseGate();
          resetClientState();
          clearResponseTimeout();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.RESPONSE_ENDED:
          aiResponseInFlightRef.current = false;
          flushDeferredAfterForward = true;
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.ERROR:
        case NUDGYT_AGENT_MESSAGE_TYPES.CANCELLED:
          aiResponseInFlightRef.current = false;
          flushDeferredAfterForward = true;
          clearStoppedResponseGate();
          break;
        case NUDGYT_AGENT_MESSAGE_TYPES.EMOTION: {
          // Emotion may precede response_start; treat it as turn-in-flight.
          aiResponseInFlightRef.current = true;
          const emotionData = aiMessage.emotion;
          if (typeof emotionData === 'object' && emotionData)
            setEmotion(emotionData);

          break;
        }
        case NUDGYT_AGENT_MESSAGE_TYPES.AUDIO_RESPONSE:
          // Handle audio playback
          enqueueAudio({
            audio: aiMessage.audio,
            viseme: aiMessage.viseme || []
          });
          break;
        default:
      }

      // Send AI message to parent application
      postMessage({
        type: MESSAGE_TYPES.CHAT,
        payload: { from: MESSAGE_FROM.AI, ...aiMessage }
      });

      if (flushDeferredAfterForward)
        queueMicrotask(() => {
          flushDeferredTokenReconnectRef.current();
        });
    };

    /**
     * WebSocket onerror event handler
     * Handles connection errors, clears timeout, and notifies parent application
     */
    ws.onerror = (error) => {
      console.debug('[DEBUG] WebSocket error occurred', { error });
      console.error('[Nudgyt Agent Client Error] WebSocket error:', error);
      clearResponseTimeout();
    };

    /**
     * WebSocket onclose event handler
     * Handles connection closure and manages reconnection logic
     * Reconnection conditions:
     * 1. Not an intentional close
     * 2. Reconnection flag is isEnable
     * 3. Haven't exceeded max reconnection attempts
     */
    ws.onclose = (event) => {
      const closedSocket = event.target;
      // Ignore closes after session token refresh socket
      if (closedSocket !== wsRef.current) {
        console.debug('[DEBUG] Ignoring close event from old socket');
        return;
      }

      console.debug('[DEBUG] WebSocket closed', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        isIntentional: isIntentionalCloseRef.current,
        shouldReconnect: shouldReconnectRef.current,
        reconnectAttempts: reconnectAttemptsRef.current
      });
      console.warn('[Nudgyt Agent WebSocket Close]', event);

      setIsWsConnected(false);
      wsRef.current = null;

      if (
        !isIntentionalCloseRef.current &&
        shouldReconnectRef.current &&
        reconnectAttemptsRef.current < nudgytAgentConfig.maxReconnectAttempts
      ) {
        handleReconnection();
      } else if (
        reconnectAttemptsRef.current >= nudgytAgentConfig.maxReconnectAttempts
      ) {
        console.error(
          '[Nudgyt Agent Client Error] Max reconnection attempts reached'
        );
        postMessage({
          type: MESSAGE_TYPES.CHAT,
          error: 'Connection failed'
        });
      } else if (!isIntentionalCloseRef.current) {
        console.error(
          '[Nudgyt Agent Client Error] Nudgyt Agent webSocket disconnected'
        );
      }

      // Reset intentional close flag
      isIntentionalCloseRef.current = false;
    };

    // Set WebSocket reference
    wsRef.current = ws;
  };

  /**
   * Applies a deferred session-token rotation after the current AI turn ends.
   * When nudgytSessionToken changes mid-reply, rotation is deferred until
   * response_ended, error, cancelled, or response timeout. If a newer token
   * is still pending, closes the live socket and reconnects with it.
   */
  const flushDeferredTokenReconnectIfNeeded = () => {
    if (!deferredTokenReconnectRef.current) return;
    if (!isEnableHookRef.current) {
      deferredTokenReconnectRef.current = false;
      return;
    }
    const desiredToken = nudgytSessionTokenRef.current;
    if (!desiredToken) {
      deferredTokenReconnectRef.current = false;
      return;
    }
    if (lastConnectedTokenRef.current === desiredToken) {
      deferredTokenReconnectRef.current = false;
      return;
    }
    deferredTokenReconnectRef.current = false;
    isIntentionalCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    const socket = wsRef.current;
    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      socket.close(1000, 'Session token rotated after AI response');
    }
    isIntentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
  };

  flushDeferredTokenReconnectRef.current = flushDeferredTokenReconnectIfNeeded;

  useEffect(() => {
    // While the avatar is hidden the scene never mounts (the parent hides the
    // iframe, so the canvas has no size), and waiting for it would leave the
    // parent's chat input disabled for the whole session.
    if (
      isWsConnected &&
      !isClientInitialized &&
      (isSceneReady || isAvatarHidden)
    ) {
      setIsClientInitialized(true);

      // If the client is initialized on iOS, we don't need to send the INITIALIZED message
      // To prevent user to send message before tapping on the screen
      if (isIOS()) return;

      postMessage({ type: MESSAGE_TYPES.INITIALIZED });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWsConnected, isClientInitialized, isSceneReady, isAvatarHidden]);

  /**
   * Effect: Enable / disable hook — teardown when disabled or unmounting.
   */
  useEffect(() => {
    console.debug('[DEBUG] Enable/disable hook effect triggered', {
      isEnableHook: isEnableHookRef.current
    });

    if (!isEnableHookRef.current) {
      console.debug('[DEBUG] Hook disabled, cleaning up state');
      deferredTokenReconnectRef.current = false;
      aiResponseInFlightRef.current = false;
      lastConnectedTokenRef.current = null;
      return;
    }

    console.debug('[DEBUG] Hook enabled, setting up connection state');
    shouldReconnectRef.current = true;
    isIntentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;

    return () => {
      shouldReconnectRef.current = false;
      isIntentionalCloseRef.current = true;
      deferredTokenReconnectRef.current = false;
      aiResponseInFlightRef.current = false;
      lastConnectedTokenRef.current = null;
      stoppedResponseRef.current = false;

      if (stopResponseFallbackTimeoutRef.current) {
        clearTimeout(stopResponseFallbackTimeoutRef.current);
        stopResponseFallbackTimeoutRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const socket = wsRef.current;
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      )
        socket.close(1000, 'Component unmounting');
    };
  }, [hasSession]);

  /**
   * Effect: Connect or rotate WebSocket when session token changes.
   * While an AI reply is in flight (response_start without response_ended), defer
   * closing the socket until response_ended so the parent can finalize transcript.
   */
  useEffect(() => {
    console.debug('[DEBUG] Session token effect triggered', {
      isEnableHook: isEnableHookRef.current,
      hasNudgytSessionToken: !!nudgytSessionToken
    });

    if (!isEnableHookRef.current || !nudgytSessionToken) {
      console.debug('[DEBUG] Early return: hook disabled or no session token', {
        isEnableHook: isEnableHookRef.current,
        hasToken: !!nudgytSessionToken
      });
      return;
    }

    const socket = wsRef.current;
    const hasLiveSocket =
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING);

    console.debug('[DEBUG] WebSocket state check', {
      hasSocket: !!socket,
      socketReadyState: socket?.readyState,
      hasLiveSocket,
      lastConnectedToken: lastConnectedTokenRef.current,
      currentToken: nudgytSessionToken,
      aiResponseInFlight: aiResponseInFlightRef.current
    });

    if (!hasLiveSocket) {
      console.debug('[DEBUG] No live socket, calling connectWebSocket');
      connectWebSocket();
      return;
    }

    if (socket.readyState === WebSocket.CONNECTING) {
      console.debug('[DEBUG] Socket is connecting, waiting...');
      return;
    }

    if (lastConnectedTokenRef.current === nudgytSessionToken) {
      console.debug('[DEBUG] Already connected with current token');
      return;
    }

    if (aiResponseInFlightRef.current) {
      console.debug('[DEBUG] AI response in flight, deferring token reconnect');
      deferredTokenReconnectRef.current = true;
      return;
    }

    console.debug('[DEBUG] Rotating WebSocket connection for new token');

    isIntentionalCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      socket.close(1000, 'Session token rotated');
    }
    isIntentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connectWebSocket uses refs
  }, [nudgytSessionToken]);

  useEffect(() => {
    if (!isEnableHookRef.current) return;

    const onParentMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (
        data?.type === MESSAGE_TYPES.CHAT &&
        data?.payload?.type === NUDGYT_AGENT_MESSAGE_TYPES.STOP_AUDIO
      ) {
        clearAudioQueue();
        setIsAiTalking(false);
        setUserText('');
        setFacialData([]);
        setDocumentIds([]);
      }
    };

    window.addEventListener('message', onParentMessage);
    return () => window.removeEventListener('message', onParentMessage);
  }, [
    hasSession,
    setUserText,
    setIsAiTalking,
    setFacialData,
    setDocumentIds,
    clearAudioQueue
  ]);

  useEffect(() => {
    if (!isEnableHookRef.current || !isStopResponseRequested) return;

    resetClientState();
    // The reply is being abandoned, so everything queued for it goes with it.
    clearAudioQueue();

    clearResponseTimeout();
    aiResponseInFlightRef.current = false;

    stoppedResponseRef.current = true;
    // Safety valve: an agent that never echoes `cancelled` would otherwise
    // leave the gate shut and mute every later reply.
    if (stopResponseFallbackTimeoutRef.current)
      clearTimeout(stopResponseFallbackTimeoutRef.current);
    stopResponseFallbackTimeoutRef.current = setTimeout(() => {
      stopResponseFallbackTimeoutRef.current = null;
      stoppedResponseRef.current = false;
    }, STOP_RESPONSE_GATE_FALLBACK_MS);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE })
      );
    }

    setIsStopResponseRequested(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStopResponseRequested, clearAudioQueue, setIsStopResponseRequested]);

  /**
   * Effect: Send user text to Nudgyt Agent
   * Triggers when user submits new text message
   * Starts timeout timer and sends text query to AI
   *
   * Deliberately does not clear the audio queue: sending another message while
   * the previous answer is still being spoken leaves it speaking, and the new
   * answer's audio queues behind it. Only Stop silences the voice.
   */
  useEffect(() => {
    if (!isEnableHookRef.current) return;

    if (
      (userText || documentIds.length > 0) &&
      isWsConnected &&
      wsRef.current
    ) {
      const userPayload = {
        type: NUDGYT_AGENT_MESSAGE_TYPES.TEXT_QUERY,
        ...(userText && { text: userText }),
        ...(documentIds.length > 0 && { document_ids: documentIds }),
        local_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
        mute_audio: false
      };

      // Send user text to Nudgyt Agent and start timeout timer
      wsRef.current.send(JSON.stringify(userPayload));
      startResponseTimeout();
      setUserText('');
      setDocumentIds([]);
    }

    //  AI conversation initiation settings including snooze configuration.
    if (userInteract && isWsConnected && wsRef.current) {
      clearAudioQueue();
      wsRef.current.send(JSON.stringify(userInteract));
      setSelectedCharacterId(characterIds[0]);
      setUserInteract(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasSession,
    userText,
    isWsConnected,
    userInteract,
    startResponseTimeout,
    clearAudioQueue
  ]);

  /**
   * Effect: Send user emotion data to Nudgyt Agent every 10 seconds
   * Only sends when we have valid emotion data from the face detector
   */
  useEffect(() => {
    if (
      !isEnableHookRef.current ||
      !isWsConnected ||
      !wsRef.current ||
      !isEmotionDetectionReady
    )
      return;

    const USER_EMOTION_INTERVAL = 10000;

    const intervalId = setInterval(() => {
      if (!userEmotion?.scores || !wsRef.current) return;

      const { neutral, happy, sad, angry, surprised } = userEmotion.scores;
      const emotionPayload = {
        type: NUDGYT_AGENT_MESSAGE_TYPES.USER_EMOTION,
        emotion: {
          neutral: neutral ?? 0,
          happy: happy ?? 0,
          sad: sad ?? 0,
          angry: angry ?? 0,
          surprised: surprised ?? 0
        }
      };

      wsRef.current.send(JSON.stringify(emotionPayload));

      postMessage({
        type: MESSAGE_TYPES.CHAT,
        payload: {
          from: MESSAGE_FROM.USER,
          type: NUDGYT_AGENT_MESSAGE_TYPES.USER_EMOTION,
          emotion: emotionPayload.emotion,
          dominant: userEmotion.dominant
        }
      });
    }, USER_EMOTION_INTERVAL);

    return () => clearInterval(intervalId);
  }, [hasSession, isWsConnected, isEmotionDetectionReady, userEmotion]);

  /**
   * Client object for avatar animation and control
   * Provides interface for lipsync, facial animation, and emotion control
   */
  const client = {
    isNudgytAgentClient: true,
    audioRef,
    isTalking: isAiTalking,
    facialData,
    emotion,
    setUserText,
    setIsAiTalking,
    isEmotionDetectionReady
  };

  return { client };
}

export default useThreeNudgytAgentClient;
